import { AfterViewInit, Component, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { PermissionEnum } from '@app/core/enums/permissions.enum';
import { FieldValidator } from '@app/core/model/other/field-validator';
import { AbstractFieldGroupBuilder, FieldStateMode } from '@app/shared/components/fields/abstract.field';
import { provideDayjsDateAdapter } from '@app/shared/extra/dayjs-date-adapter';
import { format } from '@app/shared/extra/decimal-format';
import dayjs, { Dayjs } from 'dayjs';
import { BehaviorSubject, ReplaySubject } from 'rxjs';

@Component({
  selector: 'market-value-field-builder',
  templateUrl: './market-value-field-builder.component.html',
  styleUrls: ['./market-value-field-builder.component.scss'],
  providers: [provideDayjsDateAdapter()],
  standalone: false
})
export class MarketValueFieldBuilderComponent extends AbstractFieldGroupBuilder implements OnInit, AfterViewInit {
  public Permission = PermissionEnum;
  public form: FormGroup<{ marketValue: FormControl<string>, marketValueDate: FormControl<Dayjs> }>;

  public ngOnInit(): void {
    // Cannot edit this field's group if the user has no write access to some fields in this group
    this.preconditionsForEdition &&= this.fieldGroup.children.every(fieldConfig =>
      this.accessManager.hasFieldWriteAccess(fieldConfig.field.alias, fieldConfig.fieldEntityType)
    );

    this.setFieldGroupInfo([], {
      state: new ReplaySubject<FieldStateMode>(1),
      isDisplayed: new BehaviorSubject<boolean>(false),
      isSingleField: true,
      value: {},
      initialValue: {}
    });
    this.initForm();
  }

  /**
   * Checks the current value of a field and determines which state it should be next
   * based on if the computed value can be calculated.
   * @protected
   */
  protected getNextState(): void {
    const isFieldEmpty = (field): boolean => field === void 0 || field === '' || field === null;

    const marketValue = this.getFieldGroupValue()['marketValue'];
    const isEmpty = isFieldEmpty(marketValue);

    if (isEmpty) {
      this.empty();
    } else {
      this.read();
    }
  }

  /**
   * Initialize form group with two fields marketValue and marketValueDate.
   */
  public initForm(): void {
    const fieldsConfig = this.fieldGroup.children;
    const date = dayjs(this.entity.properties['marketValueDate'] ?? '');
    this.form = new FormGroup({
      marketValue: new FormControl(
        this.entity.properties['marketValue'] ?? '',
        fieldsConfig[0].field?.validators.map(validator => this.validationService.getValidator(
          validator,
          this.entity
        ))
      ),
      marketValueDate: new FormControl(
        date.isValid() ? date : void 0,
        fieldsConfig[1].field?.validators.map(validator => this.validationService.getValidator(
          validator,
          this.entity
        ))
      )
    });

    const completeMarketValue = {
      marketValue: this.entity.properties['marketValue'],
      marketValueDate: this.entity.properties['marketValueDate']
    };

    // Perform a deep copy of the address
    this.setFieldGroupInitialValue(structuredClone(completeMarketValue));
    this.setFieldGroupValue(structuredClone(completeMarketValue));
    this.getNextState();
  }

  /**
   * Set up hooks and listen to state changes.
   */
  public ngAfterViewInit(): void {
    this.setupHooks();

    this.getFieldGroupState()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((newMode) => {
        if (newMode === FieldStateMode.AFTER_SAVE) {
          this.refreshEntity();
          // update value in form, get the value formatted
          this.form.controls.marketValue.setValue(this.entity.properties['marketValue']);
          this.setFieldGroupInitialValue(structuredClone(this.getFieldGroupValue()));
          this.getNextState();
        } else {
          this.currentMode = newMode;
        }
      });

    this.fieldGroup.children.forEach((fieldConfig) => {
      if (!fieldConfig.field.computed) {
        // If at least one member of the group is not computed, the group must be displayed
        this.getFieldGroupInfo(['isDisplayed']).next(true);
      }
    });
  }

  /**
   * Prevents typing unwanted characters in a numeric field
   * @param {InputEvent} event
   * @returns {boolean}
   */
  public validateKeypressEvent(event: InputEvent): boolean {
    const input = event.data;
    const pattern = /[0-9\-.,]/;
    return event.inputType != 'insertText' || this.validateInput(input, pattern);
  }

  /**
   * Prevents pasting unwanted characters in a numeric field
   * @param {ClipboardEvent} event
   * @returns {boolean}
   */
  public validatePasteEvent(event: ClipboardEvent): boolean {
    const input = event.clipboardData.getData('text');
    const pattern = /^(?:-?\d+|-?\d{1,3}(?:\s?\d{3})+)?(?:[,.]\d+)?$/;
    return this.validateInput(input, pattern);
  }

  public save(): void {
    const dateValue = this.form.controls.marketValueDate.value;
    const marketValue = this.form.controls.marketValue.value;
    this.setFieldGroupValue({
      marketValue: marketValue ? format(marketValue) : marketValue,
      marketValueDate: dateValue ? dayjs(dateValue).format(this.appConfig.DATE_FORMAT) : ''
    });
    super.save();
  }

  public cancel(): void {
    this.initForm();
  }

  public formatDate(date): string {
    return dayjs(date).format('MMMM YYYY');
  }

  // TODO TTT-4135 Remove, iterate over errors instead of validators
  /**
   * List of validators for which an error has been raised by the corresponding validator function.
   */
  public getErroredValidators(field: string): FieldValidator[] {
    const fieldConfig = this.fieldGroup.children.find(fieldConfig => fieldConfig.code === field);
    return fieldConfig?.field?.validators?.filter(validator => !!this.form.get(field).errors?.[validator.code])
      ?? [];
  }

  private validateInput(input: string, pattern: RegExp): boolean {
    return pattern.test(input);
  }
}
